/**
 * Deep auth lifecycle tracing (signup OTP / remount investigation).
 * Enable: REACT_APP_AUTH_DEBUG=true in DocAgent-UI/.env.local (development only).
 */
import { ROUTES } from '../constants/routes';

export const AUTH_DEBUG_ENABLED =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_AUTH_DEBUG === 'true';

const ts = () => new Date().toISOString().slice(11, 23);

function log(tag, message, data) {
  if (!AUTH_DEBUG_ENABLED) return;
  // eslint-disable-next-line no-console
  console.log(`[auth:${tag}] ${ts()} ${message}`, data ?? '');
}

/** @type {string | null} */
let fetchProbeInstalled = false;

const OTP_API_HINTS = [
  'prepare_verification',
  'attempt_verification',
  'prepare_email_address',
  'create',
  '/sign_ups',
  'email_code',
  'verification',
];

function urlLooksLikeOtpTrigger(url) {
  const u = String(url).toLowerCase();
  return OTP_API_HINTS.some((hint) => u.includes(hint));
}

/**
 * Logs Clerk Frontend API calls that typically send or rotate email OTPs.
 */
export function installClerkFetchProbe() {
  if (!AUTH_DEBUG_ENABLED || fetchProbeInstalled || typeof window === 'undefined') {
    return;
  }
  fetchProbeInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input?.url ?? '';
    const method = (init?.method || (typeof input !== 'string' ? input?.method : '') || 'GET').toUpperCase();

    if (url.includes('clerk') && urlLooksLikeOtpTrigger(url)) {
      log('otp-api', `${method} ${url}`, {
        stack: new Error('otp-api-call').stack?.split('\n').slice(2, 8).join('\n'),
      });
    }

    const response = await originalFetch(input, init);
    return response;
  };

  log('probe', 'Clerk fetch probe installed');
}

/**
 * Rewrites legacy root-level Clerk paths so navigation stays on /signup/* (same Route element).
 */
export function normalizeClerkAuthPath(to) {
  const path = typeof to === 'string' ? to.split('?')[0] : String(to);

  if (path === '/verify-email-address' || path.startsWith('/verify-email-address/')) {
    const normalized = path.replace(
      /^\/verify-email-address/,
      `${ROUTES.SIGNUP}/verify-email-address`
    );
    log('nav-normalize', `${path} → ${normalized}`);
    return typeof to === 'string' && to.includes('?')
      ? normalized + to.slice(to.indexOf('?'))
      : normalized;
  }

  return to;
}

const mountCounts = {};

export function logAuthMount(componentName) {
  if (!AUTH_DEBUG_ENABLED) return undefined;

  mountCounts[componentName] = (mountCounts[componentName] || 0) + 1;
  const id = `${componentName}-${Math.random().toString(36).slice(2, 7)}`;
  log('mount', `${componentName} MOUNT #${mountCounts[componentName]}`, {
    id,
    path: window.location.pathname,
  });

  if (componentName === 'ClerkSignUp' && mountCounts[componentName] > 1) {
    log('mount', 'WARNING: ClerkSignUp mounted more than once — likely OTP re-send', {
      count: mountCounts[componentName],
    });
  }

  return () => {
    log('mount', `${componentName} UNMOUNT`, { id, path: window.location.pathname });
  };
}

export function logAuthRouteChange(from, to, reason) {
  log('route', reason || 'location change', { from, to });
}

export function logClerkRouterNav(mode, to, normalized) {
  log('clerk-nav', `${mode} ${to}`, {
    normalized: normalized !== to ? normalized : undefined,
    path: window.location.pathname,
  });
}

export function logSignUpStateTransition(prev, next, trigger) {
  if (!AUTH_DEBUG_ENABLED) return;

  const snapshot = (su) =>
    su
      ? {
          id: su.id,
          status: su.status,
          missingFields: su.missingFields,
          unverifiedFields: su.unverifiedFields,
          verifications: su.verifications,
        }
      : null;

  const prevSnap = snapshot(prev);
  const nextSnap = snapshot(next);

  const changed =
    JSON.stringify(prevSnap) !== JSON.stringify(nextSnap) ||
    prev?.id !== next?.id ||
    prev?.status !== next?.status;

  if (!changed) return;

  log('signup-state', trigger || 'signUp updated', {
    prev: prevSnap,
    next: nextSnap,
    path: window.location.pathname,
  });
}

/** @type {string | null} */
let lastSignUpId = null;
/** @type {string | undefined} */
let lastEmailVerificationStatus = undefined;
let signUpCreateObservations = 0;

export function noteEmailVerificationPrepared(signUp, source) {
  if (!AUTH_DEBUG_ENABLED || !signUp) return;

  if (signUp.id && signUp.id !== lastSignUpId) {
    signUpCreateObservations += 1;
    lastSignUpId = signUp.id;
    log('signup-create', `new signUp.id observed (#${signUpCreateObservations})`, {
      source,
      signUpId: signUp.id,
      status: signUp.status,
      path: window.location.pathname,
    });
    if (signUpCreateObservations > 1) {
      log('signup-create', 'WARNING: multiple signUp.id — Clerk created signup more than once', {
        count: signUpCreateObservations,
      });
    }
  }

  const emailVerification = signUp.verifications?.emailAddress;
  const nextStatus = emailVerification?.status;

  if (nextStatus && nextStatus !== lastEmailVerificationStatus) {
    log('otp-state', `email verification: ${lastEmailVerificationStatus ?? '∅'} → ${nextStatus}`, {
      source,
      signUpId: signUp.id,
      path: window.location.pathname,
    });
    lastEmailVerificationStatus = nextStatus;
  }
}

export function resetAuthDebugCounters() {
  lastSignUpId = null;
  lastEmailVerificationStatus = undefined;
  signUpCreateObservations = 0;
}
